import type { CrewDay, CrewSiteRow } from "../../api/crew";
import { formatDayMonth } from "../../api/dates";

/** The small judgements My work makes about the crew's day, kept pure so
 * they are tested without a screen: which days to offer, which stop is
 * next, how a stop is described and where the directions go. */

export type Stop = CrewDay["stops"][number];

/** A site whose status says this visit's work is recorded. The office marks
 * the stop's own outcome later, from the day sheet; waiting for that before
 * moving the crew on would leave the next-stop card pointing at the chamber
 * they have just closed. A work stage ends at one of these, and the next
 * visit to the site is only planned once the office has moved it on. */
const VISIT_DONE = new Set(["assessment_completed", "service_completed", "replacement_completed", "completed"]);

export function stopDone(stop: Stop): boolean {
  return stop.outcome === "done" || (stop.outcome === "pending" && VISIT_DONE.has(stop.site.status));
}

export function stopNotDone(stop: Stop): boolean {
  return stop.outcome === "not_done";
}

/** The first stop still to do, in the planned order. */
export function nextStop(stops: readonly Stop[]): Stop | null {
  return stops.find((stop) => !stopDone(stop) && !stopNotDone(stop)) ?? null;
}

/** "Service / overhaul · Bermad DN150": the stage of the work, then the
 * valve, as far as either is known. */
export function stopMeta(site: CrewSiteRow): string {
  const valve = [site.valve_make, site.dn_scope === null ? null : `DN${site.dn_scope}`].filter((part) => part !== null && part !== "").join(" ");
  return [site.status_label, valve, site.priority === null ? null : `Red List #${site.priority}`]
    .filter((part) => part !== null && part !== "")
    .join(" · ");
}

/** Google Maps directions from wherever the phone is to the chamber. */
export function directionsUrl(lat: number, lon: number): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`;
}

function isoParts(iso: string): [number, number, number] | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return match === null ? null : [Number(match[1]), Number(match[2]), Number(match[3])];
}

/** The ISO day after `iso`, counted in UTC so no time zone can skip a day. */
export function nextDay(iso: string): string {
  const parts = isoParts(iso);
  if (parts === null) {
    return iso;
  }
  const next = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2] + 1));
  return next.toISOString().slice(0, 10);
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "Today", "Tomorrow", else "Thu 1 Oct". */
export function dayLabel(iso: string, today: string): string {
  if (iso === today) {
    return "Today";
  }
  if (iso === nextDay(today)) {
    return "Tomorrow";
  }
  const parts = isoParts(iso);
  if (parts === null) {
    return iso;
  }
  const weekday = WEEKDAYS[new Date(Date.UTC(parts[0], parts[1] - 1, parts[2])).getUTCDay()];
  return `${weekday} ${formatDayMonth(iso)}`;
}

export interface DayChoice {
  /** The `day` parameter: an ISO date, or "all". */
  value: string;
  label: string;
}

/** Today and the next two planned days as pills, then Everything. Earlier
 * planned days are still reachable, from the list the page offers beside
 * the pills, because a crew does go back to finish yesterday's. */
export function dayChoices(days: readonly string[], today: string): { pills: DayChoice[]; earlier: DayChoice[] } {
  const ahead = [...new Set([today, ...days.filter((day) => day > today)])].sort().slice(0, 3);
  const earlier = days
    .filter((day) => day < today)
    .sort()
    .reverse()
    .map((day) => ({ value: day, label: dayLabel(day, today) }));
  return {
    pills: [...ahead.map((day) => ({ value: day, label: dayLabel(day, today) })), { value: "all", label: "Everything" }],
    earlier,
  };
}

/** Straight-line kilometres between two points, for "1,2 km away". */
export function distanceKm(from: { lat: number; lon: number }, to: { lat: number; lon: number }): number {
  const radians = (deg: number) => (deg * Math.PI) / 180;
  const dLat = radians(to.lat - from.lat);
  const dLon = radians(to.lon - from.lon);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(radians(from.lat)) * Math.cos(radians(to.lat)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** "1,2 km" -- the decimal comma the rest of the app prints. */
export function formatKm(km: number): string {
  return km < 10 ? `${km.toFixed(1).replace(".", ",")} km` : `${Math.round(km)} km`;
}
